import React from "react";
import { Tooltip, Row, Col } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CalendarIcon from "assets/icons/calendar-icon.svg";
import moment from "moment";
import * as Styled from "./TopicListStyles";

interface TopicListProps {
  topics: Array<Topic>;
  isDisabled: boolean;
  bookedTopics: Array<Topic>;
  onOpenSkipTopicModal: (topicId: number) => void;
  onOpenUnskipTopicModal: (topicId: number) => void;
}

interface Topic {
  id: number;
  title: string;
  cities: Array<City>;
  category: Category;
  studied: boolean;
  skipped: boolean;
  studiedCount: number;
  beingBookedOn: boolean;
}
interface City {
  name: string;
}
interface Category {
  name: string;
  skippedIfSpokenOnly: boolean;
}

const TopicList = ({
  topics,
  bookedTopics,
  isDisabled,
  onOpenSkipTopicModal,
  onOpenUnskipTopicModal,
}: TopicListProps) => {
  const { t } = useTranslation("my_level");

  const bookedTopicsID: Array<number> = bookedTopics?.map(
    (topic: Topic) => topic?.id
  );

  if (topics.length === 0) {
    return <p>{t("There's no topic for this chapter")}.</p>;
  }

  const handleClickTopicCheckbox = (topic: Topic, isBooked: boolean) => {
    if (isDisabled || topic.studied || isBooked) return;

    if (topic.skipped) {
      if (topic.category.skippedIfSpokenOnly) {
        return;
      }

      onOpenUnskipTopicModal(topic.id);
    } else {
      onOpenSkipTopicModal(topic.id);
    }
  };

  // RENDERERS
  const renderCityTags = (cities: Array<City>, isBooked: boolean) => (
    <>
      {cities.map((city) => (
        <Styled.TopicCityTag
          isDisabled={isDisabled}
          isBooked={isBooked}
          key={city.name}
        >
          {city?.name}
        </Styled.TopicCityTag>
      ))}
    </>
  );

  const renderBookedClassTime = (topic: Topic) => {
    let outputVariable = null;

    /* eslint-disable no-unused-expressions */
    bookedTopics?.forEach((aTopic: Topic) => {
      if (topic.id === aTopic.id) {
        outputVariable = aTopic.beingBookedOn;
      }
    });
    /* eslint-enable no-unused-expressions */

    // returns tag
    if (outputVariable) {
      return (
        <Styled.TopicCalendarTag>
          {moment
            .parseZone(outputVariable)
            .format("ddd HH:mm [-] DD MMMM YYYY")
            .toUpperCase()}
        </Styled.TopicCalendarTag>
      );
    }

    return null;
  };

  const renderCategory = (topic: Topic, isBooked: boolean) => {
    // grey out the category tag if student skipped a class they haven't studied
    const greyedOut = topic.skipped && !topic.studied;

    return (
      <Styled.TopicCategoryTag
        isDisabled={isDisabled || greyedOut}
        isBooked={isBooked}
      >
        {topic.category?.name.toUpperCase()}
      </Styled.TopicCategoryTag>
    );
  };

  const renderCheckmark = (numberOfCheckmarks: number) => {
    // Always render one checkmark
    const checkmark = (
      <Styled.Checkmark isDisabled={isDisabled} index={0} key={0} total={1}>
        &#10003;
      </Styled.Checkmark>
    );

    // If more than 1, show badge of the number of checkmarks
    return (
      <>
        {checkmark}
        {numberOfCheckmarks > 1 && (
          <Styled.Badge>{numberOfCheckmarks}</Styled.Badge>
        )}
      </>
    );
  };

  const renderCrossmark = () => <Styled.Crossmark>&#10005;</Styled.Crossmark>;

  const renderCalendar = () => {
    return <img src={CalendarIcon} alt="calendar" />;
  };

  const checkCurrentTopic = (topic: Topic) => {
    return bookedTopicsID?.includes(topic.id);
  };

  const renderTopicWrapper = (topic: Topic, isBooked: boolean) => {
    return (
      <Styled.TopicWrapper key={topic.title}>
        <Row>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Styled.TopicLeftWrapper>
              <Link to={`/topic-details/${topic.id}`}>
                <Styled.TopicTitle
                  studied={topic.studied}
                  isDisabled={isDisabled}
                  isBooked={isBooked}
                  skipped={topic.skipped}
                  htmlFor="topic"
                  role="presentation"
                >
                  {topic.title}
                </Styled.TopicTitle>
              </Link>
              <Styled.Checkbox
                onClick={() => handleClickTopicCheckbox(topic, isBooked)}
                studied={topic.studied}
                isDisabled={isDisabled}
                isBooked={isBooked}
              >
                {isBooked && renderCalendar()}
                {!isBooked &&
                  topic.studied &&
                  renderCheckmark(topic.studiedCount)}
                {!isBooked &&
                  topic.skipped &&
                  !topic.studied &&
                  renderCrossmark()}
              </Styled.Checkbox>
            </Styled.TopicLeftWrapper>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Styled.TopicRightWrapper>
              {renderBookedClassTime(topic)}
              {renderCityTags(topic.cities, isBooked)}
              {renderCategory(topic, isBooked)}
            </Styled.TopicRightWrapper>
          </Col>
        </Row>
      </Styled.TopicWrapper>
    );
  };

  return (
    <>
      {topics.map((topic: Topic) => {
        if (topic.skipped && topic.category.skippedIfSpokenOnly) {
          return (
            <Tooltip
              title={t(
                "This topic was skipped because you signed up for Spoken Only"
              )}
              key={topic.id}
            >
              {renderTopicWrapper(topic, checkCurrentTopic(topic))}
            </Tooltip>
          );
        }

        return renderTopicWrapper(topic, checkCurrentTopic(topic));
      })}
    </>
  );
};

export default TopicList;
